/**
 * TextInterface - Handles text chat UI and functionality
 * Part of the unified TTPChatWidget
 */

import { createGalleryHandler } from './galleryHandler.js';
function panelSolidBackground(panel) {
  const bg = panel?.backgroundColor;
  if (typeof bg !== 'string' || !bg.startsWith('#')) return '#FFFFFF';
  if (bg.length === 7) return bg;
  if (bg.length === 4) {
    const r = bg[1];
    const g = bg[2];
    const b = bg[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return '#FFFFFF';
}

function panelIsLightHex(bg) {
  if (!bg || bg.charAt(0) !== '#') return true;
  const r = parseInt(bg.substr(1, 2), 16);
  const g = parseInt(bg.substr(3, 2), 16);
  const b = parseInt(bg.substr(5, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 160;
}

/** RGB for shadows / typing dots from a solid-ish CSS color (hex or first hex in string). */
function rgbTupleFromChromeColor(color, fallbackTuple) {
  const fb = fallbackTuple || [124, 58, 237];
  if (!color || typeof color !== 'string') return fb;
  const t = color.trim();
  if (t.startsWith('#') && t.length === 7) {
    const n = parseInt(t.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const m = t.match(/#[0-9a-fA-F]{6}/i);
  if (m) {
    const n = parseInt(m[0].slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  return fb;
}

/** Textarea single-line height and max auto-grow (px); keep in sync with `.message-input` CSS. */
const TEXT_INPUT_MIN_HEIGHT_PX = 36;
const TEXT_INPUT_MAX_HEIGHT_PX = 132;

/** Resolves voice primary / gradient strings to #rrggbb for CSS hex+alpha suffixes. */
function firstHexFromVoiceColor(c, fallback) {
  if (c == null || typeof c !== 'string') return fallback;
  const t = c.trim();
  if (t.startsWith('#') && t.length === 7) return t;
  if (t.startsWith('#') && t.length === 4) {
    const r = t[1];
    const g = t[2];
    const b = t[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  const m = t.match(/#[0-9a-fA-F]{6}/i);
  return m ? m[0] : fallback;
}

export class TextInterface {
  constructor(config, sdk) {
    this.config = config;
    this.sdk = sdk;
    this.streamingEl = null;
    this.hasStartedStreaming = false;
    this.isActive = false;
    // Shadow root reference for DOM queries
    this.shadowRoot = config.shadowRoot || document;
    // Store event handlers to allow cleanup
    this.eventHandlers = {
      autoResize: null,
      keydown: null
    };
    // Track if event handlers are already set up to avoid unnecessary re-initialization
    this.eventHandlersSetup = false;
  }

  // Helper function to get translated text
  t(key) {
    const lang = this.config.language || 'en';
    const translations = this.config.translations?.[lang] || this.config.translations?.en || {};
    return translations[key] || key;
  }

  /**
   * Generate HTML for text interface
   */
  generateHTML() {
    // Use text config, fallback to panel config, then translation, then default
    const inputPlaceholder = this.config.inputPlaceholder || 
                            this.config.panel?.inputPlaceholder || 
                            this.t('typeMessage') || 
                            'Type your message...';
    const unifiedMode = this.config.behavior?.mode === 'unified';
    const backArrow = this.config.direction === 'rtl' ? '→' : '←';
    const backBar = unifiedMode ? `
      <div class="text-interface-top-bar">
        <button type="button" class="text-interface-back-btn" id="textUnifiedBackBtn" aria-label="${this.t('back')}">
          <span class="text-interface-back-icon" aria-hidden="true">${backArrow}</span>
          <span class="text-interface-back-label">${this.t('back')}</span>
        </button>
        <button type="button" class="ttp-info-btn" aria-label="${this.t('aboutTitle') || 'About'}" title="${this.t('aboutTitle') || 'About'}">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <line x1="12" y1="11" x2="12" y2="16"/>
            <circle cx="12" cy="7.7" r="1.05" fill="currentColor" stroke="none"/>
          </svg>
        </button>
      </div>` : '';
    return `<div class="text-interface" id="textInterface">
      ${backBar}
      <div class="messages-container" id="messagesContainer">
        <div class="empty-state">
          <div class="empty-state-icon">💬</div>
          <div class="empty-state-title">${this.t('hello')}</div>
          <div class="empty-state-text">${this.t('sendMessage')}</div>
        </div>
      </div>
      <div class="input-container">
        <div class="input-wrapper" style="flex:1;">
          <textarea class="message-input" id="messageInput" placeholder="${inputPlaceholder}" rows="1" dir="${this.config.direction || 'ltr'}"></textarea>
        </div>
        <button class="send-button" id="sendButton" aria-label="${this.t('sendMessageAria')}">${this.config.sendButtonText || '➤'}</button>
        ${(this.config.sendButtonHint?.text || this.config.panel?.sendButtonHint?.text) ? `
          <div class="send-button-hint" style="color: ${this.config.sendButtonHint?.color || this.config.panel?.sendButtonHint?.color || '#6B7280'}; font-size: ${this.config.sendButtonHint?.fontSize || this.config.panel?.sendButtonHint?.fontSize || '14px'}; text-align: center; margin-top: 4px;">
            ${this.config.sendButtonHint?.text || this.config.panel?.sendButtonHint?.text}
          </div>
        ` : ''}
      </div>
    </div>`;
  }

  /**
   * Generate CSS for text interface
   */
  generateCSS() {
    const messages = this.config.messages;
    const panel = this.config.panel;
    const anim = this.config.animation;
    const useVoiceTheme = this.config.useVoiceTheme !== false;
    const voice = this.config.voice || {};

    // Use text config, fallback to panel config for backward compatibility
    const sendButtonColor = this.config.sendButtonColor || this.config.panel?.sendButtonColor || '#7C3AED';
    const sendButtonHoverColor = this.config.sendButtonHoverColor || this.config.panel?.sendButtonHoverColor || '#6D28D9';
    const sendButtonTextColor = this.config.sendButtonTextColor || this.config.panel?.sendButtonTextColor || '#FFFFFF';
    const inputPlaceholder = this.config.inputPlaceholder || this.config.panel?.inputPlaceholder || 'Type your message...';
    const inputFontSize = this.config.inputFontSize || this.config.panel?.inputFontSize || '14px';
    const inputBorderRadius = this.config.inputBorderRadius || this.config.panel?.inputBorderRadius || 20;
    const inputPadding = this.config.inputPadding || this.config.panel?.inputPadding || '6px 14px';

    let panelLight;
    let topBarBg;
    let topBarBorder;
    let messagesAreaBg;
    let backBtnColor;
    let backBtnHoverBg;
    let backBtnHoverColor;
    let emptyMuted;
    let emptyTitle;
    let inputContainerBg;
    let inputContainerBorderTop;
    let inputBorderColor;
    let inputFocusColor;
    let inputBackgroundColor;
    let inputTextColor;
    let inputFocusBg;
    let inputFocusBoxShadow;
    let placeholderColor;
    let userBubbleBg;
    let userBubbleTextColor;
    let agentBubbleBg;
    let agentBubbleTextColor;
    let agentBubbleBorder;
    let avatarAgentBg;
    let avatarUserBg;
    let avatarUserColor;
    let avatarSize;
    let typingDotAlpha;
    let errorBubbleBg;
    let errorBubbleColor;
    let errorBubbleBorder;

    if (useVoiceTheme) {
      const p1 = firstHexFromVoiceColor(
        voice.primaryBtnGradient1 || voice.startCallButtonColor,
        '#6d56f5',
      );
      const p2 = firstHexFromVoiceColor(
        voice.primaryBtnGradient2 || voice.startCallButtonColor,
        '#9d8df8',
      );
      const a1 = firstHexFromVoiceColor(voice.avatarGradient1, p1);
      const a2 = firstHexFromVoiceColor(voice.avatarGradient2, p2);

      panelLight = false;
      topBarBg = 'transparent';
      topBarBorder = 'rgba(255,255,255,0.16)';
      messagesAreaBg = 'rgba(255,255,255,0.09)';
      backBtnColor = 'rgba(255,255,255,0.92)';
      backBtnHoverBg = 'rgba(255,255,255,0.14)';
      backBtnHoverColor = '#ffffff';
      emptyMuted = 'rgba(255,255,255,0.7)';
      emptyTitle = '#ffffff';
      inputContainerBg = 'transparent';
      inputContainerBorderTop = 'rgba(255,255,255,0.16)';
      inputBorderColor = 'rgba(255,255,255,0.26)';
      inputFocusColor = `${p1}e6`;
      inputBackgroundColor = 'rgba(255,255,255,0.12)';
      inputTextColor = '#f8fafc';
      inputFocusBg = 'rgba(255,255,255,0.18)';
      inputFocusBoxShadow = `0 0 0 3px ${p1}59`;
      placeholderColor = 'rgba(255,255,255,0.55)';
      userBubbleBg = `${p1}73`;
      userBubbleTextColor = '#ffffff';
      agentBubbleBg = 'rgba(255,255,255,0.16)';
      agentBubbleTextColor = '#f8fafc';
      agentBubbleBorder = '1px solid rgba(255,255,255,0.28)';
      avatarAgentBg = `linear-gradient(135deg, ${a1}, ${a2})`;
      avatarUserBg = 'rgba(255,255,255,0.22)';
      avatarUserColor = 'rgba(255,255,255,0.95)';
      avatarSize = '26px';
      typingDotAlpha = '0.92';
      errorBubbleBg = 'rgba(254, 202, 202, 0.28)';
      errorBubbleColor = '#fee2e2';
      errorBubbleBorder = '1px solid rgba(252, 165, 165, 0.45)';
    } else {
      const panelBgSolid = panelSolidBackground(panel);
      panelLight = panelIsLightHex(panelBgSolid);
      topBarBg = panelBgSolid;
      topBarBorder = panelLight ? '#E5E7EB' : 'rgba(255,255,255,0.08)';
      messagesAreaBg = panelLight ? '#f8fafc' : 'rgba(255,255,255,0.04)';
      backBtnColor = panelLight ? '#475569' : 'rgba(255,255,255,0.78)';
      backBtnHoverBg = panelLight ? '#f1f5f9' : 'rgba(255,255,255,0.08)';
      backBtnHoverColor = panelLight ? '#334155' : '#ffffff';
      emptyMuted = panelLight ? '#64748b' : 'rgba(255,255,255,0.45)';
      emptyTitle = panelLight ? '#334155' : 'rgba(255,255,255,0.92)';
      inputContainerBg = panelBgSolid;
      inputContainerBorderTop = topBarBorder;
      inputBorderColor = this.config.inputBorderColor || this.config.panel?.inputBorderColor || '#E5E7EB';
      inputFocusColor = this.config.inputFocusColor || this.config.panel?.inputFocusColor || '#7C3AED';
      inputBackgroundColor = this.config.inputBackgroundColor || this.config.panel?.inputBackgroundColor || '#FFFFFF';
      inputTextColor = this.config.inputTextColor || this.config.panel?.inputTextColor || '#1F2937';
      inputFocusBg =
        inputBackgroundColor === '#FFFFFF' ? '#FFFFFF' : inputBackgroundColor;
      inputFocusBoxShadow = `0 0 0 3px ${inputFocusColor}33`;
      placeholderColor = '#9CA3AF';
      userBubbleBg = messages.userBackgroundColor;
      userBubbleTextColor = messages.userTextColor || messages.textColor;
      agentBubbleBg = messages.agentBackgroundColor;
      agentBubbleTextColor = messages.agentTextColor || messages.textColor;
      const accentRgbLocal = rgbTupleFromChromeColor(sendButtonColor);
      agentBubbleBorder = `1px solid rgba(${accentRgbLocal[0]}, ${accentRgbLocal[1]}, ${accentRgbLocal[2]}, ${panelLight ? 0.14 : 0.22})`;
      avatarAgentBg = 'transparent';
      avatarUserBg = 'transparent';
      avatarUserColor = 'inherit';
      avatarSize = '20px';
      typingDotAlpha = panelLight ? '0.45' : '0.65';
      errorBubbleBg = messages.errorBackgroundColor;
      errorBubbleColor = '#991B1B';
      errorBubbleBorder = 'none';
    }

    const accentRgb = rgbTupleFromChromeColor(sendButtonColor);
    
    // Add !important to display rules when not using Shadow DOM (to override theme CSS)
    const important = this.config.useShadowDOM === false ? ' !important' : '';
    
    return `
      .text-interface-top-bar {
        flex-shrink: 0${important};
        padding: 10px 12px 8px${important};
        border-bottom: 1px solid ${topBarBorder}${important};
        background: ${topBarBg}${important};
        display: flex${important};
        align-items: center${important};
        justify-content: space-between${important};
      }
      .text-interface-top-bar .ttp-info-btn {
        color: ${backBtnColor}${important};
      }
      .text-interface-top-bar .ttp-info-btn:hover {
        background: ${backBtnHoverBg}${important};
        color: ${backBtnHoverColor}${important};
      }
      .text-interface-back-btn {
        display: inline-flex${important};
        align-items: center${important};
        gap: 6px${important};
        padding: 6px 10px${important};
        margin: 0${important};
        border: none${important};
        border-radius: 8px${important};
        background: transparent${important};
        color: ${backBtnColor}${important};
        font-size: 16px${important};
        font-weight: 600${important};
        cursor: pointer${important};
        font-family: inherit${important};
      }
      .text-interface-back-btn:hover {
        background: ${backBtnHoverBg}${important};
        color: ${backBtnHoverColor}${important};
      }
      .text-interface-back-icon {
        font-size: 18px${important};
        line-height: 1${important};
      }
      .text-interface-back-label {
        line-height: 1.2${important};
      }
      /* Messages container using new classes */
      #messagesContainer { 
        flex: 1${important}; 
        overflow-y: auto${important}; 
        overflow-x: hidden${important}; 
        padding: 20px${important}; 
        background: ${messagesAreaBg}${important}; 
        display: flex${important}; 
        flex-direction: column${important}; 
        gap: 16px${important}; 
        min-height: 0${important}; 
      }
      .empty-state { 
        flex: 1${important}; 
        display: flex${important}; 
        flex-direction: column${important}; 
        align-items: center${important}; 
        justify-content: center${important}; 
        gap: 12px${important}; 
        color: ${emptyMuted}${important}; 
        text-align: center${important}; 
        padding: 20px${important}; 
      }
      .empty-state-icon { font-size: 52px${important}; opacity: 0.3${important}; }
      .empty-state-title { font-size: 22px${important}; font-weight: 700${important}; color: ${emptyTitle}${important}; }
      .empty-state-text { font-size: 15px${important}; max-width: 300px${important}; line-height: 1.45${important}; }

      .text-interface { 
        display: none${important}; 
        flex: 1${important}; 
        flex-direction: column${important}; 
        min-height: 0${important}; 
        overflow: hidden${important}; 
      }
      .text-interface.active { display: flex${important}; }
      
      .message { 
        display: flex${important}; 
        gap: 8px${important}; 
        padding: 4px 0${important}; 
        max-width: 100%${important}; 
        align-items: center${important}; 
      }
      .message.edge-left { flex-direction: row${important}; }
      .message.edge-right { flex-direction: row-reverse${important}; }
      .message-bubble { 
        padding: 14px 16px${important}; 
        border-radius: ${messages.borderRadius}px${important}; 
        max-width: 80%${important}; 
        font-size: ${messages.fontSize}${important}; 
        line-height: 1.45${important};
        word-wrap: break-word${important}; 
        text-align: ${this.config.direction === 'rtl' ? 'right' : 'left'}${important}; 
        direction: ${this.config.direction || 'ltr'};
      }
      .message.user .message-bubble { 
        background: ${userBubbleBg}${important}; 
        color: ${userBubbleTextColor}${important}; 
      }
      .message.agent .message-bubble { 
        background: ${agentBubbleBg}${important}; 
        color: ${agentBubbleTextColor}${important}; 
        border: ${agentBubbleBorder}${important}; 
      }

      .message.user { 
        align-self: ${this.config.direction === 'rtl' ? 'flex-start' : 'flex-end'}${important}; 
      }
      .message.agent { 
        align-self: ${this.config.direction === 'rtl' ? 'flex-end' : 'flex-start'}${important}; 
      }
      .message .message-bubble { 
        text-align: ${this.config.direction === 'rtl' ? 'right' : 'left'} !important; 
      }
      ${this.config.direction === 'rtl' ? `
      .message-bubble {
        text-align: right !important;
      }
      ` : ''}
      .message-avatar { 
        width: ${avatarSize}${important}; 
        height: ${avatarSize}${important}; 
        min-width: ${avatarSize}${important}; 
        border-radius: 50%${important}; 
        display: flex${important}; 
        align-items: center${important}; 
        justify-content: center${important}; 
        flex-shrink: 0${important}; 
        color: inherit${important}; 
        font-size: ${useVoiceTheme ? '14' : '20'}px${important}; 
        line-height: 1${important}; 
        background: transparent${important}; 
        border: none${important}; 
        box-sizing: border-box${important}; 
      }
      .message-avatar.user { background: ${avatarUserBg}${important}; color: ${avatarUserColor}${important}; }
      .message-avatar.agent { background: ${avatarAgentBg}${important}; }
      
      .message.system {
        background: ${messages.systemBackgroundColor};
        align-self: flex-start${important};
      }
      .message.error {
        background: ${messages.errorBackgroundColor};
        align-self: flex-start${important};
      }
      
      .input-container {
        display: flex${important};
        gap: 8px${important};
        padding: 12px 16px${important};
        background: ${inputContainerBg}${important};
        border-top: 1px solid ${inputContainerBorderTop}${important};
        align-items: center${important};
        flex-shrink: 0${important};
        flex-direction: ${this.config.direction === 'rtl' ? 'row-reverse' : 'row'}${important};
      }
      
      .input-wrapper {
        position: relative${important};
        display: flex${important};
        align-items: center${important};
      }
      
      .message-input {
        width: 100%${important};
        min-height: ${TEXT_INPUT_MIN_HEIGHT_PX}px${important};
        max-height: ${TEXT_INPUT_MAX_HEIGHT_PX}px${important};
        padding: ${inputPadding};
        border: 1px solid ${inputBorderColor};
        border-radius: ${inputBorderRadius}px;
        font-size: ${inputFontSize};
        font-family: inherit${important};
        line-height: 1.4${important};
        resize: none${important};
        overflow-y: auto${important};
        background: ${inputBackgroundColor};
        color: ${inputTextColor};
        vertical-align: top${important};
        margin: 0${important};
        display: block${important};
        white-space: pre-wrap${important};
        word-wrap: break-word${important};
        text-align: ${this.config.direction === 'rtl' ? 'right' : 'left'}${important};
        direction: ${this.config.direction || 'ltr'};
        -webkit-appearance: none${important};
        appearance: none${important};
        box-sizing: border-box${important};
      }
      
      .message-input:focus {
        outline: none${important};
        border-color: ${inputFocusColor};
        background: ${inputFocusBg}${important};
        box-shadow: ${inputFocusBoxShadow};
      }
      
      .message-input::placeholder {
        color: ${placeholderColor}${important};
        text-align: ${this.config.direction === 'rtl' ? 'right' : 'left'}${important};
      }
      
      .send-button {
        width: 44px${important};
        height: 44px${important};
        border-radius: 50%${important};
        border: none${important};
        background: ${sendButtonColor};
        color: ${sendButtonTextColor};
        font-size: ${this.config.sendButtonFontSize || this.config.panel?.sendButtonFontSize || '20px'};
        font-weight: ${this.config.sendButtonFontWeight || this.config.panel?.sendButtonFontWeight || '500'};
        cursor: pointer${important};
        display: flex${important};
        align-items: center${important};
        justify-content: center${important};
        flex-shrink: 0${important};
        transition: all 0.2s ease${important};
        box-shadow: 0 4px 12px rgba(${accentRgb[0]}, ${accentRgb[1]}, ${accentRgb[2]}, 0.32)${important};
      }
      
      .send-button:hover:not(:disabled) {
        background: ${sendButtonHoverColor};
        transform: scale(1.05)${important};
        box-shadow: 0 6px 16px rgba(${accentRgb[0]}, ${accentRgb[1]}, ${accentRgb[2]}, 0.42)${important};
      }
      
      .send-button-hint {
        width: 100%${important};
        text-align: center${important};
        margin-top: 4px${important};
      }
      
      .send-button:disabled {
        opacity: 0.5${important};
        cursor: not-allowed${important};
      }
      
      .typing-indicator {
        display: inline-flex${important};
        gap: 4px${important};
        align-items: center${important};
      }
      
      .typing-dot {
        width: 6px${important};
        height: 6px${important};
        border-radius: 50%${important};
        background: rgba(${accentRgb[0]}, ${accentRgb[1]}, ${accentRgb[2]}, ${typingDotAlpha})${important};
        animation: typingDot 1.4s ease-in-out infinite${important};
      }
      
      .typing-dot:nth-child(2) { animation-delay: 0.2s${important}; }
      .typing-dot:nth-child(3) { animation-delay: 0.4s${important}; }
      
      @keyframes typingDot {
        0%, 60%, 100% { transform: translateY(0); opacity: 0.7; }
        30% { transform: translateY(-8px); opacity: 1; }
      }
      
      .error-message {
        padding: 12px${important};
        background: ${errorBubbleBg};
        border-radius: ${messages.borderRadius}px;
        color: ${errorBubbleColor}${important};
        border: ${errorBubbleBorder}${important};
        font-size: ${messages.fontSize}${important};
        margin: 8px 0${important};
      }
      
      ${useVoiceTheme ? `
      #textInterface.active .input-container .send-button-hint {
        color: rgba(255,255,255,0.72)${important};
      }
      ` : ''}
      
      @media (max-width: 768px) {
        #messagesContainer {
          padding: 12px${important};
          gap: 12px${important};
        }
        
        .message-bubble {
          max-width: 85%${important};
          font-size: ${messages.fontSize}${important};
          padding: 12px 14px${important};
        }
        
        .text-input-container {
          padding: 10px${important};
          gap: 8px${important};
        }
        
        #text-chat-input {
          font-size: 16px !important; /* Prevents iOS zoom on focus */
          padding: 10px 14px${important};
          min-height: 44px${important};
        }
        
        #text-chat-send {
          min-width: 56px${important};
          min-height: 44px${important};
          width: 56px${important};
          height: 44px${important};
        }
        
        .empty-state-icon {
          font-size: 44px${important};
        }
        
        .empty-state-title {
          font-size: 20px${important};
        }
        
        .empty-state-text {
          font-size: 14px${important};
        }
      }
      
      @media (max-width: 480px) {
        #messagesContainer {
          padding: 10px${important};
          gap: 10px${important};
        }
        
        .message-bubble {
          max-width: 90%${important};
          font-size: ${messages.fontSize}${important};
          padding: 10px 12px${important};
        }
        
        .text-input-container {
          padding: 8px${important};
        }
        
        #text-chat-input {
          font-size: 16px !important;
          padding: 8px 12px${important};
        }
      }
    `;
  }

  /**
   * Setup event handlers for text interface
   */
  setupEventHandlers() {
    const sendButton = this.shadowRoot.getElementById('sendButton');
    const inputField = this.shadowRoot.getElementById('messageInput');
    const newChatBtn = this.shadowRoot.getElementById('newChatBtn');
    
    if (sendButton) sendButton.onclick = () => this.sendMessage();
    if (newChatBtn) newChatBtn.onclick = () => this.startNewChat();
    
    // Update placeholder and direction when config changes
    this.updateInputAttributes();
    
    // Send on Enter key, auto-resize textarea, and keep cursor visible
    if (inputField) {
      // Set initial height
      inputField.style.height = `${TEXT_INPUT_MIN_HEIGHT_PX}px`;
      inputField.style.overflow = 'hidden';
      
      // Auto-resize textarea as user types
      const autoResize = () => {
        inputField.style.height = 'auto';
        const newHeight = Math.min(inputField.scrollHeight, TEXT_INPUT_MAX_HEIGHT_PX);
        inputField.style.height = newHeight + 'px';
        inputField.style.overflowY = newHeight >= TEXT_INPUT_MAX_HEIGHT_PX ? 'auto' : 'hidden';
        
        // When at max height with overflow, scroll to bottom to keep cursor visible
        if (newHeight >= TEXT_INPUT_MAX_HEIGHT_PX) {
          // Small delay to ensure height is applied first
          setTimeout(() => {
            inputField.scrollTop = inputField.scrollHeight;
          }, 0);
        }
      };
      
      inputField.addEventListener('input', autoResize);
      
      inputField.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.sendMessage();
        } else if (e.key === 'Enter' && e.shiftKey) {
          // Allow Enter with Shift for new line, resize after
          setTimeout(autoResize, 0);
        }
      });
    }
  }

  /**
   * Start new chat
   */
  startNewChat() {
    try { 
      localStorage.removeItem('ttp_text_chat_conversation_id'); 
    } catch (_) {}
    
    if (this.sdk) {
      this.sdk.config.conversationId = null;
    }
    
    // Reset UI messages to empty state
    const container = this.shadowRoot.getElementById('messagesContainer');
    if (container) {
      container.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon">💬</div>
          <div class="empty-state-title">${this.t('hello')}</div>
          <div class="empty-state-text">${this.t('sendMessage')}</div>
        </div>`;
    }
    
    // Focus message input
    const input = this.shadowRoot.getElementById('messageInput');
    if (input) input.focus();
  }

  /**
   * Update input attributes (placeholder, direction) when config changes
   */
  updateInputAttributes() {
    const inputField = this.shadowRoot.getElementById('messageInput');
    if (inputField) {
      // Update placeholder based on current language
      const inputPlaceholder = this.config.inputPlaceholder || 
                              this.config.panel?.inputPlaceholder || 
                              this.t('typeMessage') || 
                              'Type your message...';
      inputField.placeholder = inputPlaceholder;
      
      // Update direction
      inputField.dir = this.config.direction || 'ltr';
      
      // Update text-align style
      inputField.style.textAlign = this.config.direction === 'rtl' ? 'right' : 'left';
    }
  }

  /**
   * Show text interface
   */
  show() {
    const textInterface = this.shadowRoot.getElementById('textInterface');
    if (textInterface) textInterface.classList.add('active');
    
    // Focus input soon after render
    setTimeout(() => {
      const input = this.shadowRoot.getElementById('messageInput');
      if (input) input.focus();
    }, 50);
  }

  /**
   * Hide text interface
   */
  hide() {
    const textInterface = this.shadowRoot.getElementById('textInterface');
    if (textInterface) textInterface.classList.remove('active');
  }

  /**
   * Start chat session
   */
  async startChat() {
    // No-op with single-shot design; connection happens per message
    this.isActive = true;
    this.updateSendButtonState();
  }

  /**
   * Send message
   */
  async sendMessage() {
    const input = this.shadowRoot.getElementById('messageInput');
    const text = input.value.trim();
    
    if (!text) {
      return;
    }
    
    // Ensure active state
    if (!this.isActive) {
      await this.startChat();
    }
    
    // Add user message to UI
    this.addMessage('user', text);
    
    // Clear input and reset height
    input.value = '';
    input.style.height = `${TEXT_INPUT_MIN_HEIGHT_PX}px`;
    input.style.overflow = 'hidden';
    
    // Prepare streaming bubble and send via SDK
    try {
      this.beginStreaming();
      await this.sdk.sendMessage(text);
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      // Check if this is a domain error - if so, it's already handled by the domainError event handler
      // Don't show it here to avoid duplicates
      const isDomainError = error && (
        error.message === 'DOMAIN_NOT_WHITELISTED' ||
        (error.message && error.message.includes('Domain not whitelisted'))
      );
      
      if (!isDomainError) {
        // Only show non-domain errors here (domain errors are handled by event handler)
        this.showError(error);
      }
      this.stopStreamingState();
    }
  }

  /**
   * Update send button state
   */
  updateSendButtonState() {
    const sendButton = this.shadowRoot.getElementById('sendButton');
    if (!sendButton) return;
    
    sendButton.disabled = !this.isActive; // single-shot; enable when ready for input
  }

  /**
   * Add message to UI
   */
  addMessage(type, text) {
    const messages = this.shadowRoot.getElementById('messagesContainer');
    if (!messages) return;
    
    // Remove empty state if present
    const emptyState = messages.querySelector('.empty-state');
    if (emptyState) {
      emptyState.remove();
    }
    
    const message = document.createElement('div');
    const edgeClass = (this.config.direction === 'rtl')
      ? (type === 'user' ? 'edge-left' : 'edge-right')
      : (type === 'user' ? 'edge-right' : 'edge-left');
    message.className = `message ${type} ${edgeClass}`;

    const avatar = document.createElement('div');
    avatar.className = `message-avatar ${type}`;
    const avatarIcon = type === 'user' 
      ? (this.config.messages?.userAvatarIcon || '👤')
      : (this.config.messages?.agentAvatarIcon || '🤖');
    avatar.textContent = avatarIcon;

    const bubble = document.createElement('div');
    bubble.className = 'message-bubble';
    bubble.textContent = text;

    // Order is controlled by edgeClass via flex-direction
    message.appendChild(avatar);
    message.appendChild(bubble);
    messages.appendChild(message);
    messages.scrollTop = messages.scrollHeight;
  }

  /**
   * Begin streaming response
   */
  beginStreaming() {
    const messages = this.shadowRoot.getElementById('messagesContainer');
    if (!messages) return;

    // Clean any previous indicator
    this.stopStreamingState();

    const el = document.createElement('div');
    const edgeClass = (this.config.direction === 'rtl') ? 'edge-right' : 'edge-left';
    el.className = `message agent ${edgeClass}`;
    el.id = 'agent-streaming';

    const avatar = document.createElement('div');
    avatar.className = 'message-avatar agent';
    avatar.textContent = this.config.messages?.agentAvatarIcon || '🤖';

    const bubble = document.createElement('div');
    bubble.className = 'message-bubble';
    // show typing dots until first chunk
    bubble.innerHTML = '<span class="typing-indicator"><span class="typing-dot"></span><span class="typing-dot"></span><span class="typing-dot"></span></span>';

    el.appendChild(avatar);
    el.appendChild(bubble);
    messages.appendChild(el);
    this.streamingEl = bubble;
    this.hasStartedStreaming = false;
    this._pendingMedia = []; // Buffer for images that arrive before next text chunk
    messages.scrollTop = messages.scrollHeight;
  }

  /**
   * Append chunk to streaming response.
   * Does NOT flush buffered media here — media is flushed only when the
   * next show_media arrives or on done. This ensures all the descriptive
   * text for a set of images stays together above the images.
   */
  appendStreamingChunk(chunk) {
    if (!this.streamingEl) return;

    if (!this.hasStartedStreaming) {
      // remove typing indicator on first content
      this.streamingEl.textContent = '';
      this.hasStartedStreaming = true;
    }

    // Append text — use textNode if we have inline media DOM elements
    const hasInlineMedia = this.streamingEl.querySelector('.inline-media-gallery');
    if (hasInlineMedia) {
      this.streamingEl.appendChild(document.createTextNode(chunk));
    } else {
      this.streamingEl.textContent += chunk;
    }

    const messages = this.shadowRoot.getElementById('messagesContainer');
    if (messages) {
      messages.scrollTop = messages.scrollHeight;
    }
  }

  /**
   * Buffer media images to render after the descriptive text.
   * When a new show_media arrives, flush the PREVIOUS buffered media first
   * (the text describing those images has finished), then buffer the new one.
   * Final flush happens on done.
   *
   * Flow:
   *   text: "Our Standard Room is..."  → append text
   *   show_media: [standard imgs]      → buffer (no previous to flush)
   *   text: "...beautiful. Our Deluxe" → append text (images still buffered)
   *   show_media: [deluxe imgs]        → flush standard imgs, buffer deluxe
   *   text: "...Room is spacious."     → append text
   *   done                             → flush deluxe imgs
   */
  appendStreamingMedia(images, title) {
    if (!this.streamingEl) return;

    if (!this.hasStartedStreaming) {
      this.streamingEl.textContent = '';
      this.hasStartedStreaming = true;
    }

    // Flush previous media — the text describing those images is complete
    this._flushPendingMedia();

    // Buffer new media (will be flushed on next show_media or done)
    this._pendingMedia = this._pendingMedia || [];
    this._pendingMedia.push({ images, title });
  }

  /**
   * Flush buffered media into the streaming bubble.
   */
  _flushPendingMedia() {
    if (!this._pendingMedia || this._pendingMedia.length === 0) return;
    if (!this.streamingEl) return;

    for (const { images, title } of this._pendingMedia) {
      this._renderInlineMedia(images, title);
    }
    this._pendingMedia = [];
  }

  /**
   * Render inline image gallery in the streaming bubble.
   */
  _renderInlineMedia(images, title) {
    const gallery = document.createElement('div');
    gallery.className = 'inline-media-gallery';
    gallery.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;margin:8px 0;';

    for (const img of images) {
      const card = document.createElement('div');
      card.style.cssText = 'flex:1;min-width:80px;max-width:150px;border-radius:8px;overflow:hidden;cursor:pointer;';

      const imgEl = document.createElement('img');
      imgEl.src = img.url;
      imgEl.alt = img.caption || title || '';
      imgEl.style.cssText = 'width:100%;height:80px;object-fit:cover;display:block;';
      imgEl.loading = 'lazy';

      // Open fullscreen gallery viewer on click (same as voice gallery)
      imgEl.addEventListener('click', () => {
        if (!this._inlineGalleryHandler) {
          this._inlineGalleryHandler = createGalleryHandler(this._widget);
        }
        this._inlineGalleryHandler.handleShowMedia({ images, title });
      });

      card.appendChild(imgEl);

      if (img.caption) {
        const caption = document.createElement('div');
        caption.textContent = img.caption;
        caption.style.cssText = 'font-size:10px;padding:2px 4px;text-align:center;background:#f5f5f5;color:#333;';
        card.appendChild(caption);
      }

      gallery.appendChild(card);
    }

    this.streamingEl.appendChild(gallery);

    const messages = this.shadowRoot.getElementById('messagesContainer');
    if (messages) {
      messages.scrollTop = messages.scrollHeight;
    }
  }

  /**
   * Finalize streaming response.
   * Flushes any remaining buffered media, then finalizes the bubble.
   * If inline media was rendered during streaming, we keep the DOM structure
   * (text nodes + gallery elements) instead of overwriting with textContent.
   */
  finalizeStreaming(fullText) {
    if (this.streamingEl) {
      // Flush any remaining buffered media
      this._flushPendingMedia();

      // If we have inline media (child elements beyond text), don't overwrite with textContent
      // as that would destroy the gallery DOM elements.
      const hasInlineMedia = this.streamingEl.querySelector('.inline-media-gallery');
      if (!hasInlineMedia) {
        this.streamingEl.textContent = fullText || this.streamingEl.textContent;
      }
      // If hasInlineMedia, keep the DOM as-is — it already has text nodes + gallery elements

      const container = this.shadowRoot.getElementById('agent-streaming');
      if (container) container.id = '';
      this.streamingEl = null;
      this._pendingMedia = [];
    }
    this.updateSendButtonState();
  }

  /**
   * Stop streaming state
   */
  stopStreamingState() {
    const existing = this.shadowRoot.getElementById('agent-streaming');
    if (existing) existing.remove();
    this.streamingEl = null;
    this.hasStartedStreaming = false;
  }

  /**
   * Show error message
   */
  showError(error) {
    const messages = this.shadowRoot.getElementById('messagesContainer');
    if (!messages) return;
    
    // Check if this is a domain validation error
    const isDomainError = error && (
      error.message === 'DOMAIN_NOT_WHITELISTED' ||
      (typeof error === 'string' && error.includes('DOMAIN_NOT_WHITELISTED')) ||
      (error.message && error.message.includes('Domain not whitelisted')) ||
      (typeof error === 'string' && error.includes('Domain not whitelisted'))
    );
    
    if (isDomainError) {
      // Show domain error with title and message
      const errorContainer = document.createElement('div');
      errorContainer.className = 'error-message';
      errorContainer.style.cssText = 'padding: 16px; margin: 12px; border-radius: 8px; background: ' + 
        (this.config.messages?.errorBackgroundColor || '#FEE2E2') + ';';
      
      const title = document.createElement('div');
      title.style.cssText = 'font-weight: 600; font-size: 17px; margin-bottom: 8px; color: #991B1B;';
      title.textContent = this.t('domainNotValidated');
      
      const message = document.createElement('div');
      message.style.cssText = 'font-size: 16px; color: #991B1B; line-height: 1.5;';
      message.textContent = this.t('domainErrorMessage');
      
      errorContainer.appendChild(title);
      errorContainer.appendChild(message);
      messages.appendChild(errorContainer);
    } else {
      // Show regular error
      const errorEl = document.createElement('div');
      errorEl.className = 'error-message';
      errorEl.textContent = typeof error === 'string' ? error : (error?.message || error);
      messages.appendChild(errorEl);
    }
    
    messages.scrollTop = messages.scrollHeight;
  }

  /**
   * Set active state
   */
  setActive(active) {
    this.isActive = active;
    this.updateSendButtonState();
  }
}

